/**
 * Main Application Component
 *
 * Sets up routing and global layout.
 */

import React, { Suspense, lazy } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useComparison } from './contexts/ComparisonContext';

// Lazy load pages for code splitting
const ItemList = lazy(() => import('./pages/ItemList'));
const ItemDetail = lazy(() => import('./pages/ItemDetail'));
const ComparisonView = lazy(() => import('./pages/ComparisonView'));

// Loading fallback
function LoadingSpinner() {
  return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Loading...</p>
    </div>
  );
}

// Header component
function Header() {
  const { user, isAuthenticated, login, logout } = useAuth();
  const { items: comparisonItems } = useComparison();
  const location = useLocation();

  return (
    <header className="header">
      <div className="header-container">
        <Link to="/" className="header-logo">
          <span className="logo-icon">MHF</span>
          <span className="logo-text">MustHaveFeatures</span>
        </Link>

        <nav className="header-nav">
          <Link
            to="/"
            className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
          >
            Browse
          </Link>

          <Link
            to="/compare"
            className={`nav-link compare-link ${location.pathname === '/compare' ? 'active' : ''}`}
          >
            Compare
            {comparisonItems.length > 0 && (
              <span className="compare-badge">{comparisonItems.length}</span>
            )}
          </Link>
        </nav>

        <div className="header-actions">
          {isAuthenticated ? (
            <div className="user-menu">
              <span className="user-name">{user?.name || user?.email}</span>
              <button onClick={logout} className="btn btn-secondary btn-sm">
                Logout
              </button>
            </div>
          ) : (
            <button onClick={login} className="btn btn-primary btn-sm">
              Login
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

// Footer component
function Footer() {
  return (
    <footer className="footer">
      <div className="footer-container">
        <p className="footer-text">
          MustHaveFeatures - Compare with confidence
        </p>
        <nav className="footer-nav">
          <a href="/privacy" className="footer-link">Privacy</a>
          <a href="/terms" className="footer-link">Terms</a>
          <a href="/contact" className="footer-link">Contact</a>
        </nav>
      </div>
    </footer>
  );
}

// 404 Page
function NotFound() {
  return (
    <div className="not-found">
      <h1>404</h1>
      <p>Page not found</p>
      <Link to="/" className="btn btn-primary">
        Back to Home
      </Link>
    </div>
  );
}

// Main App component
function App() {
  return (
    <div className="app">
      <Header />

      <main className="main">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<ItemList />} />
            <Route path="/item/:uuid" element={<ItemDetail />} />
            <Route path="/compare" element={<ComparisonView />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
    </div>
  );
}

export default App;
